/**
 * 電話でのお問い合わせのアコーディオン
 */
export class TelAccordion {
  private button: HTMLButtonElement | null;
  private wrapper: HTMLElement | null;
  private isAnimating = false;

  constructor() {
    this.button = document.getElementById('js-tell__button') as HTMLButtonElement | null;
    this.wrapper = document.getElementById('js-tell__wrapper') as HTMLElement | null;

    if (this.button && this.wrapper) {
      this.init();
    }
  }

  private init(): void {
    if (!this.button || !this.wrapper) return;

    // 初期状態を閉じた状態に設定
    this.button.setAttribute('aria-expanded', 'false');
    this.wrapper.style.display = 'none';
    this.wrapper.style.overflow = 'hidden';

    // ボタンのクリックイベントを設定
    this.button.addEventListener('click', () => {
      this.toggle();
    });

    // ハッシュに応じて初期状態を設定
    this.handleInitialHash();

    // ハッシュ変更時のイベントリスナーを設定
    window.addEventListener('hashchange', () => {
      this.handleHashChange();
    });
  }

  /**
   * ページ読み込み時のハッシュに応じて開閉状態を設定
   */
  private handleInitialHash(): void {
    const hash = window.location.hash.replace('#', '');

    if (hash === 'enquiryByTel') {
      // 電話でのお問い合わせを開く
      this.openWithoutAnimation();
    }
    // enquiryByFormやco-formTopの場合は既にデフォルトで閉じているので何もしない
  }

  /**
   * ハッシュ変更時の処理（アニメーション付き）
   */
  private handleHashChange(): void {
    const hash = window.location.hash.replace('#', '');
    const isExpanded = this.button?.getAttribute('aria-expanded') === 'true';

    if (hash === 'enquiryByTel') {
      // 電話でのお問い合わせを開く
      if (!isExpanded) {
        this.open();
      }
    } else if (hash === 'enquiryByForm' || hash === 'co-formTop') {
      // フォームのハッシュの場合は閉じる
      if (isExpanded) {
        this.close();
      }
    }
  }

  private toggle(): void {
    if (!this.button || !this.wrapper || this.isAnimating) return;

    const isExpanded = this.button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.close();
    } else {
      this.open();
    }
  }

  private open(): void {
    if (!this.button || !this.wrapper || this.isAnimating) return;

    this.isAnimating = true;
    this.button.setAttribute('aria-expanded', 'true');

    // 高さを測定するために一時的に表示
    this.wrapper.style.display = 'block';
    this.wrapper.style.height = '0';
    const height = this.wrapper.scrollHeight;

    // アニメーション開始
    this.wrapper.style.transition = 'height 0.6s ease';

    // 次のフレームで高さを設定（リフローを強制）
    requestAnimationFrame(() => {
      this.wrapper!.style.height = `${height}px`;
    });

    // アニメーション終了後の処理
    let hasEnded = false;
    const onTransitionEnd = () => {
      if (this.wrapper && !hasEnded) {
        hasEnded = true;
        this.wrapper.style.height = 'auto';
        this.wrapper.style.transition = '';
        this.isAnimating = false;
        this.wrapper.removeEventListener('transitionend', onTransitionEnd);
      }
    };

    this.wrapper.addEventListener('transitionend', onTransitionEnd);

    // フォールバック: transitionendが発火しない場合に備えて
    setTimeout(() => {
      if (!hasEnded) {
        onTransitionEnd();
      }
    }, 700); // 600ms + 100msのマージン
  }

  private close(): void {
    if (!this.button || !this.wrapper || this.isAnimating) return;

    this.isAnimating = true;
    this.button.setAttribute('aria-expanded', 'false');

    // 現在の高さを設定
    const height = this.wrapper.scrollHeight;
    this.wrapper.style.height = `${height}px`;

    // 次のフレームで高さを0に（リフローを強制）
    requestAnimationFrame(() => {
      if (this.wrapper) {
        this.wrapper.style.transition = 'height 0.6s ease';
        this.wrapper.style.height = '0';
      }
    });

    // アニメーション終了後の処理
    let hasEnded = false;
    const onTransitionEnd = () => {
      if (this.wrapper && !hasEnded) {
        hasEnded = true;
        this.wrapper.style.display = 'none';
        this.wrapper.style.transition = '';
        this.wrapper.style.height = '';
        this.isAnimating = false;
        this.wrapper.removeEventListener('transitionend', onTransitionEnd);
      }
    };

    this.wrapper.addEventListener('transitionend', onTransitionEnd);

    // フォールバック: transitionendが発火しない場合に備えて
    setTimeout(() => {
      if (!hasEnded) {
        onTransitionEnd();
      }
    }, 700); // 600ms + 100msのマージン
  }

  /**
   * アニメーションなしで開く（ハッシュ遷移時などに使用）
   */
  public openWithoutAnimation(): void {
    if (!this.button || !this.wrapper) return;

    this.button.setAttribute('aria-expanded', 'true');
    this.wrapper.style.display = 'block';
    this.wrapper.style.height = 'auto';
  }

  /**
   * アニメーションなしで閉じる
   */
  public closeWithoutAnimation(): void {
    if (!this.button || !this.wrapper) return;

    this.button.setAttribute('aria-expanded', 'false');
    this.wrapper.style.display = 'none';
    this.wrapper.style.height = '';
  }
}
